// UserInfoInput.js
import React, { useEffect, useState } from 'react';
import { Button, DialogContent, DialogActions, CircularProgress, Grid, Popover } from '@mui/material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { AppWord, AppObject, AppStyle, UserClass } from '../../value/AppConst';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import HowToRegIcon from '@mui/icons-material/HowToReg'; // check user id
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import InputUtil from '../../value/InputUtil';
import VzmTextField from '../../common/VzmTextField';
import VzmCheckbox from '../../common/VzmCheckbox';
import { ErrorHintBox } from '../../common/VzmStyled';
import DaumAddressSearch from '../../common/DaumAddressSearch';
import PhoneField from '../../common/PhoneField';
import AutoTextField from '../../common/AutoTextField';
import VzmSelector from '../../common/VzmSelector';
import { UserInfoDictionary } from './UserInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import {
    comInfoListRepo, dcInfoListRepo, storeInfoListRepo, authClassListRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_STORE_INFO } from '../store_info/StoreInfoGql';
import { LIST_DC_INFO } from '../dc_info/DcInfoGql';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import { LIST_AUTH_CLASS } from '../auth_class/AuthClassGql';
import { CHECK_USER_ID } from './UserInfoGql';
import ValueMisc from '../../value/ValueMisc';
import { ComInfoDictionary } from '../com_info/ComInfoDict';
import { DcInfoDictionary } from '../dc_info/DcInfoDict';
import { StoreInfoDictionary } from '../store_info/StoreInfoDict';
import IsValid from '../../value/IsValid';

const InputMap = UserInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);
const storeClassId = UserClass.store.id.toString();
/**
 * (USER_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-19 14:22:13 using DbdbTap
 */
export default function UserInfoInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const storeInfoListData = useReactiveVar(storeInfoListRepo);
    const dcInfoListData = useReactiveVar(dcInfoListRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const authClassListData = useReactiveVar(authClassListRepo);
    const [anchorAddr, setAnchorAddr] = useState(null);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    // ##### Call GraphQL to get List #####
    const [getDcInfoList, responseDcInfoList] = useLazyQuery(LIST_DC_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetDcInfoList(data, option)},
		onError: (error) => addAlert({title: DcInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getStoreInfoList, responseStoreInfoList] = useLazyQuery(LIST_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreInfoList(data, option)},
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getComInfoList, responseComInfoList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComInfoList(data, option)},
		onError: (error) => addAlert({title: ComInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getAuthClassList, responseAuthClassList] = useLazyQuery(LIST_AUTH_CLASS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetAuthClassList(data, option)},
		onError: (error) => addAlert({title: '사용자 권한 목록 조회 오류', message: [error.message]})
    });
    const [checkUserId, responseCheckUserId] = useLazyQuery(CHECK_USER_ID, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteCheckUserId(data, option)},
        onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.CheckUserId, message: [error.message]})
    });

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.error.storeId = true; // authClassId가 점포이므로 storeId는 필수.
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    const lazyGetOrgList = (authClassId) => {
        if(ValueMisc.isTruckCompanyClassId(authClassId)
            && (comInfoListData.records.length < 1 || ValueMisc.isOlderInSec(comInfoListData.when, 1200))) {
            getComInfoList();
        }
        else if(ValueMisc.isStoreClassId(authClassId)
            && (storeInfoListData.records.length < 1 || ValueMisc.isOlderInSec(storeInfoListData.when, 1200))) {
            getStoreInfoList();
        }
        else if(dcInfoListData.records.length===0 || ValueMisc.isOlderInSec(dcInfoListData.when, 1200)) {
            getDcInfoList();
        }
    }

    useEffect(()=>{
        if(open) {
            // lazy query to get select options depending on the user's class.
            if(item) {
                lazyGetOrgList(item.authClassId);
            }
            else {
                lazyGetOrgList(storeClassId);
            }
            if(authClassListData.records.length===0 || ValueMisc.isOlderInSec(authClassListData.when, 7200)) {
                getAuthClassList();
            }
        }
    }, [open, item, authClassListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetDcInfoList = (data, clientOption) => {
        if(data.dcInfoList) {
            dcInfoListRepo({when:new Date().getTime()/1000, records:data.dcInfoList});
        }
    };
    const onCompleteGetStoreInfoList = (data, clientOption) => {
        if(data.storeInfoList) {
            storeInfoListRepo({when:new Date().getTime()/1000, records:data.storeInfoList});
        }
    };
    const onCompleteGetComInfoList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };
    const onCompleteGetAuthClassList = (data, clientOption) => {
        if(data.authClassList) {
            authClassListRepo({when:new Date().getTime()/1000, records:data.authClassList});
        }
    };
    const onCompleteCheckUserId = (data, clientOption) => {
        if(data.userInfoCheckUserId) {
            if(data.userInfoCheckUserId.userNid > 0) {
                addAlert({title: '아이디 중복', message: ['이미 사용 중인 아이디입니다.']});
                return;
            }
        }
        addAlert({title: '아이디 사용 가능', message: ['사용 가능한 아이디입니다.']});
    };

    const onClickCheckUserId = () => {
        checkUserId({variables:{userId:input.data.userId}});
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);

        if(id === InputMap.authClassId.id) {
            lazyGetOrgList(value);
            if(ValueMisc.isStoreClassId(value) || ValueMisc.isTruckCompanyClassId(value)) {
                newInput.data.allDcYn = 'N';
            }
            newInput.error.storeId = false;
            newInput.error.comId = false;
            newInput.error.dcCd = false;
            const dataId = ValueMisc.isStoreClassId(value)
                ? InputMap.storeId.id
                : ValueMisc.isTruckCompanyClassId(value)
                ? InputMap.comId.id
                : InputMap.dcCd.id;
            if(IsValid.text(newInput.data[dataId])) {
                newInput.hasError = Object.values(newInput.error).some((e)=>e);
            }
            else {
                newInput.error[dataId] = true;
                newInput.hasError = true;
            }
        }
        else if(id===InputMap.password.id || id===InputMap.passwordConfirm.id || id===InputMap.newPassword.id) {
            if(isEdit) {
                if(IsValid.text(newInput.data.newPassword)) {
                    if(newInput.data.newPassword !== newInput.data.passwordConfirm) {
                        newInput.error.passwordConfirm = true;
                        newInput.hasError = true;
                    }
                    else {
                        newInput.error.passwordConfirm = false;
                        newInput.hasError = Object.values(newInput.error).some((e)=>e);
                    }
                }
            }
            else {
                if(IsValid.text(newInput.data.password)) {
                    if(newInput.data.password !== newInput.data.passwordConfirm) {
                        newInput.error.passwordConfirm = true;
                        newInput.hasError = true;
                    }
                    else {
                        newInput.error.passwordConfirm = false;
                        newInput.hasError = Object.values(newInput.error).some((e)=>e);
                    }
                }
            }
        }
        setInput(newInput);
    };

    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !InputMap.zipCd.check(addr.zipCd), InputMap, isEdit);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !InputMap.addr.check(addr.addr), InputMap, isEdit);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        if(ValueMisc.isStoreClassId(param.authClassId)) {
            param.comId = null;
            param.dcCd = null;
        }
        else if(ValueMisc.isTruckCompanyClassId(param.authClassId)) {
            param.storeId = null;
            param.dcCd = null;
        }
        else {
            param.storeId = null;
            param.comId = null;
        }

        if(isEdit) {
            if(IsValid.text(input.data.newPassword) && input.data.newPassword !== input.data.passwordConfirm) {
                addAlert({title: '암호 불일치', message: ['암호와 암호확인이 일치하지 않습니다.']});
                return;
            }
        }

        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave
        || responseDcInfoList.loading || responseStoreInfoList.loading || responseComInfoList.loading
        || responseAuthClassList.loading || responseCheckUserId.loading;

    return(
        <DraggableEditor open={open} maxWidth='sm'
            onClose={onClickCancel}
            startIcon={<AccountBoxIcon fontSize={AppWord.LARGE} style={{color:'white', marginRight:15}}/>}
            title={isEdit ? '사용자 정보 수정' : '신규 사용자 등록'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.userName}
                            value={input.data.userName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <VzmTextField
                            dict={InputMap.userId}
                            value={input.data.userId}
                            onChange={onChangeValue}
                            disabled={isEdit}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        {
                            isEdit ? null :
                            <Button
                                onClick={onClickCheckUserId}
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                startIcon={<HowToRegIcon/>}
                                disabled={!input.data.userId}
                            >중복확인</Button>
                        }
                    </Grid>
                    {
                        isEdit && !sessionInfo.isAdmin() ? null :
                        <>
                        <Grid item xs={6}>
                            {
                                isEdit
                                ?
                                <VzmTextField
                                    dict={InputMap.newPassword}
                                    value={input.data.newPassword}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                                :
                                <VzmTextField
                                    dict={InputMap.password}
                                    value={input.data.password}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            }
                        </Grid>
                        <Grid item xs={6}>
                            <VzmTextField
                                dict={InputMap.passwordConfirm}
                                value={input.data.passwordConfirm}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </Grid>
                        </>
                    }

                    <Grid item xs={4}>
                        <VzmSelector
                            dict={InputMap.authClassId}
                            value={input.data.authClassId}
                            onChange={onChangeValue}
                            list={
                                authClassListData.records
                                .filter((ac)=>ac.showOnList && parseInt(ac.authClassId) >= sessionInfo.user.authClassInt)
                            }
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={12}>
                        <AutoTextField
                            dict={InputMap.comId}
                            value={input.data.comId}
                            onChange={onChangeValue}
                            list={comInfoListData.records}
                            disabled={!ValueMisc.isTruckCompanyClassId(input.data.authClassId)}
                            fullWidth={true}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AutoTextField
                            dict={InputMap.storeId}
                            value={input.data.storeId}
                            onChange={onChangeValue}
                            list={storeInfoListData.records}
                            disabled={!ValueMisc.isStoreClassId(input.data.authClassId)}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmSelector
                            dict={InputMap.dcCd}
                            value={input.data.dcCd}
                            onChange={onChangeValue}
                            list={dcInfoListData.records}
                            disabled={ValueMisc.isStoreClassId(input.data.authClassId) || ValueMisc.isTruckCompanyClassId(input.data.authClassId)}
                            fullWidth={true}
                        />
                    </Grid>


                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.zipCd}
                            value={input.data.zipCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            style={{marginLeft:8}} size={AppWord.LARGE}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                        >
                            주소찾기
                        </Button>
                        <Popover
                            anchorEl={anchorAddr}
                            open={Boolean(anchorAddr)}
                            onClose={()=>{setAnchorAddr(null);}}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                        >
                            <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                        </Popover>
                    </Grid>

                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.addr}
                            value={input.data.addr}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>


                    <Grid item xs={6}>
                        <PhoneField
                            dict={InputMap.telno}
                            value={input.data.telno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={6}></Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.email}
                            value={input.data.email}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <VzmCheckbox
                            dict={InputMap.allDcYn}
                            value={input.data.allDcYn}
                            onChange={onChangeValue}
                            disabled={
                                ValueMisc.isStoreClassId(input.data.authClassId)
                                || ValueMisc.isTruckCompanyClassId(input.data.authClassId)
                            }
                        />
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
